import { supabase } from '@/lib/supabase';
import type { AssistantDocument } from '@/pages/dashboard/apps/mon-assistant/types';

// ─────────────────────────────────────────────────────────────
// assistantChat — appels et historique de l'assistant marchand.
//
// Regroupe :
//  • l'appel de l'edge function `mon-assistant` avec une extraction
//    HONNÊTE du message d'erreur (fini le « Edge Function returned a
//    non-2xx status code » sans explication) ;
//  • le chargement de l'historique enregistré (table monassistantshop),
//    partagé par la page complète ET le dock flottant, pour que les
//    conversations survivent au rechargement de la page.
// ─────────────────────────────────────────────────────────────

export interface AssistantReply {
  reply: string;
  invoice?: AssistantDocument;
  article?: { id: number | null; title: string; slug: string; status: string };
}

interface RawPayload {
  reply?: string;
  error?: string;
  invoice?: { id?: number; number?: string; docType?: string } | null;
  article?: { id?: number | null; title?: string; slug?: string; status?: string } | null;
}

/** Extrait le message d'erreur réel renvoyé par l'edge function. */
async function extractError(fnError: unknown): Promise<string> {
  const err = fnError as { message?: string; context?: Response } | null;
  let message = err?.message || "Impossible de contacter l'assistant";
  if (message.includes('non-2xx')) {
    message = "L'assistant n'a pas pu répondre. Reformulez votre demande dans un instant.";
  }
  try {
    const ctx = err?.context;
    if (ctx && typeof ctx.clone === 'function') {
      const res = ctx.clone();
      const text = await res.text();
      if (text) {
        try {
          const parsed = JSON.parse(text) as { error?: string; message?: string };
          const real = parsed?.error || parsed?.message;
          if (real) message = real;
        } catch {
          if (text.length < 240 && !text.includes('<')) message = text;
        }
      }
    }
  } catch {
    /* message générique conservé */
  }
  return message;
}

/** Appelle l'assistant et renvoie la réponse (ou lève une erreur lisible). */
export async function invokeAssistant(body: Record<string, unknown>): Promise<AssistantReply> {
  const { data, error: fnError } = await supabase.functions.invoke('mon-assistant', { body });
  if (fnError) throw new Error(await extractError(fnError));

  const payload = (data || null) as RawPayload | null;
  const reply = payload?.reply;
  if (!reply) throw new Error(payload?.error || "Réponse vide de l'assistant");

  const created = payload?.invoice;
  const invoice = created && created.id && created.number
    ? {
        id: Number(created.id),
        number: String(created.number),
        docType: created.docType === 'quote' ? ('quote' as const) : ('invoice' as const),
      }
    : undefined;

  const art = payload?.article;
  const article = art && art.title
    ? {
        id: art.id ?? null,
        title: String(art.title),
        slug: String(art.slug || ''),
        status: String(art.status || 'published'),
      }
    : undefined;

  return { reply: String(reply), invoice, article };
}

export interface StoredMessage {
  role: 'user' | 'assistant';
  content: string;
  date: string;
  /** Note vocale enregistrée avec le message (URL persistante). */
  audio?: { url: string; duration: number };
}

/** Historique enregistré (ordre chronologique), prêt à afficher. */
export async function loadAssistantHistory(userId: number, limit = 200): Promise<StoredMessage[]> {
  const uid = Number(userId);
  if (!Number.isFinite(uid) || uid <= 0) return [];
  try {
    const { data, error } = await supabase
      .from('monassistantshop')
      .select('messageshop, reponseai, date, audio_url, audio_duration')
      .eq('iduser', uid)
      .order('date', { ascending: true })
      .limit(limit);
    if (error) throw error;

    const messages: StoredMessage[] = [];
    for (const row of data || []) {
      const question = String(row.messageshop ?? '').trim();
      const answer = String(row.reponseai ?? '').trim();
      const date = String(row.date ?? '');
      const audioUrl = String(row.audio_url ?? '').trim();
      const audio = audioUrl
        ? { url: audioUrl, duration: Number(row.audio_duration) || 0 }
        : undefined;
      if (question) messages.push({ role: 'user', content: question, date, audio });
      if (answer) messages.push({ role: 'assistant', content: answer, date });
    }
    return messages;
  } catch {
    return [];
  }
}